import { ComingSoonTag, Icon, TextEllipsis } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import {
  Box,
  Stack,
  Switch,
  SwitchProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { mergeSx } from 'merge-sx';
import { memo } from 'react';
import { ExportTemplateItemBadge } from '../export-template-item-badge';
import * as Styled from './styles';
import * as SX from './sx';
import type { TemplateOptionProps as Props } from './types';

export const TemplateOption = memo(
  ({
    className,
    option,
    checkedValues,
    categoryWithNoChildren,
    sx,
    onToggle,
  }: Props) => {
    const theme = useTheme();
    const handleChangeSwitch: SwitchProps['onChange'] = () => {
      onToggle();
    };

    if (option.hidden) {
      return null;
    }

    return (
      <Styled.Wrapper
        className={classnames('TemplateOption', className)}
        sx={mergeSx(SX.root, sx)}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
      >
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          overflow="hidden"
          gap={categoryWithNoChildren ? 0.5 : 1}
        >
          {option.icon && (
            <Stack>
              <Icon
                name={option.icon}
                color={
                  categoryWithNoChildren
                    ? theme.palette.primary[500]
                    : theme.palette.grey[400]
                }
                size={16}
              />
            </Stack>
          )}

          <Stack direction="row" alignItems="center" overflow="hidden" gap={1}>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              overflow="hidden"
            >
              <Typography
                className="OptionName"
                sx={SX.name}
                variant="body3"
                fontWeight={400}
                color={theme.palette.grey[700]}
              >
                {option.name}
              </Typography>
              {option.description && (
                <TextEllipsis
                  variant="body3"
                  fontWeight={400}
                  color={theme.palette.grey[400]}
                >
                  {' '}
                  - {option.description}
                </TextEllipsis>
              )}
            </Stack>

            {option.infoBadge ? (
              <Tooltip
                arrow
                placement="bottom"
                maxWidth="unset"
                title={option.infoBadge.tooltip}
              >
                <Box display="flex" alignItems="center">
                  <Icon
                    name={option.infoBadge.icon ?? 'info-circle-outline'}
                    size={18}
                    sxx={SX.icon}
                  />
                </Box>
              </Tooltip>
            ) : (
              option.hasInfoBadge && (
                <ExportTemplateItemBadge checkedValues={checkedValues} />
              )
            )}
          </Stack>
        </Stack>

        {option.comingSoon ? (
          <ComingSoonTag size="small" />
        ) : (
          <Box sx={SX.baseSwitch}>
            <Switch
              checked={option.selected}
              disabled={option.disabled}
              onChange={handleChangeSwitch}
              size="small"
            />
          </Box>
        )}
      </Styled.Wrapper>
    );
  }
);

TemplateOption.displayName = 'TemplateOption';
